import type { DraftPayment, Payment } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { type ApolloError, gql } from "@apollo/client";
import { useMutation } from "@apollo/client";

type BackfillDraftPaymentsInput = {
  invoiceIds: string[];
};

export const useBackfillDraftPaymentDetails = (): [
  (input: BackfillDraftPaymentsInput) => Promise<DraftPayment[]>,
  {
    data: any;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { tenantId } = useTenant();
  const [backfillDraftPayments, { data, loading, error }] = useMutation<{
    backfillDraftPayments: DraftPayment[];
  }>(BACKFILL_DRAFT_PAYMENTS_MUTATION);

  const handleBackfillDraftPayments = async (
    input: BackfillDraftPaymentsInput,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    const { data } = await backfillDraftPayments({
      variables: {
        tenantId,
        invoiceIds: input.invoiceIds,
      },
    });

    return data?.backfillDraftPayments ?? [];
  };

  return [handleBackfillDraftPayments, { data, loading, error }];
};

export const BACKFILL_DRAFT_PAYMENTS_MUTATION = gql`
  mutation BackfillDraftPayments($tenantId: String!, $invoiceIds: [String!]!) {
    backfillDraftPayments(tenantId: $tenantId, invoiceIds: $invoiceIds) {
      draftPaymentId
      status
      amount
      currency
      recipientName
      recipientIban
      purpose
      fullPurpose
      executionDate
      createdAt
      invoices {
        invoiceId
        invoiceNo
        totalAmount
      }
    }
  }
`;
